import React from 'react';
import { Link } from 'react-router-dom';
import { Smartphone, ShoppingBag, Heart, Bell, MessageSquare, Database, DollarSign, BarChart, Check, ArrowRight } from 'lucide-react';

const FEATURE_IMAGES = [
  "https://customer-assets.emergentagent.com/job_23b5f19d-1a39-4e03-91cd-806fad7065f2/artifacts/f0d495xz_IMG_0087.jpeg",
  "https://customer-assets.emergentagent.com/job_23b5f19d-1a39-4e03-91cd-806fad7065f2/artifacts/wsnus7nz_IMG_0085.jpeg",
  "https://customer-assets.emergentagent.com/job_23b5f19d-1a39-4e03-91cd-806fad7065f2/artifacts/40bb3269_IMG_0086.jpeg",
  "https://customer-assets.emergentagent.com/job_23b5f19d-1a39-4e03-91cd-806fad7065f2/artifacts/jorp3yrh_IMG_0089.webp",
  "https://customer-assets.emergentagent.com/job_23b5f19d-1a39-4e03-91cd-806fad7065f2/artifacts/9acm0pfi_IMG_0090.jpeg",
  "https://customer-assets.emergentagent.com/job_23b5f19d-1a39-4e03-91cd-806fad7065f2/artifacts/jdzhx8rp_IMG_0093.jpeg",
  "https://customer-assets.emergentagent.com/job_23b5f19d-1a39-4e03-91cd-806fad7065f2/artifacts/9p6b3kun_IMG_0092.jpeg",
  "https://customer-assets.emergentagent.com/job_23b5f19d-1a39-4e03-91cd-806fad7065f2/artifacts/jmr0girf_IMG_0091.png",
];

const KIOSK_IMAGE = "https://customer-assets.emergentagent.com/job_23b5f19d-1a39-4e03-91cd-806fad7065f2/artifacts/ajyim3rn_IMG_0111.png";

export const Features = () => {
  const features = [
    { icon: <Smartphone size={40} />, title: 'Custom Branded Mobile App (PWA)', description: 'A beautiful mobile app with your branding, colors, and logo. Works on any smartphone without App Store downloads.', benefits: ['Instant access - no downloads required', 'Works on iPhone and Android', 'Add to home screen like a native app', 'Fast loading and offline capability'], stat: 'Works on 100% of smartphones' },
    { icon: <ShoppingBag size={40} />, title: 'Online Ordering', description: 'Accept pickup and dine-in orders directly through your app. Keep 100% of every sale.', benefits: ['Zero commission fees', 'Real-time order notifications', 'Menu management dashboard', 'Customizable ordering hours'], stat: 'Save 15-30% on every order' },
    { icon: <Heart size={40} />, title: 'Loyalty & Rewards Program', description: 'Turn first-time customers into regulars with a proven points-based loyalty system.', benefits: ['Automated point tracking', 'Customizable reward tiers', 'Birthday rewards and bonuses', 'Gamification to drive engagement'], stat: '30% increase in repeat visits' },
    { icon: <Bell size={40} />, title: 'Push Notifications', description: 'Reach customers instantly with promotions, specials, and important updates.', benefits: ['Instant delivery to customer phones', 'Schedule campaigns in advance', 'Targeted messaging by customer segment', 'Track open rates and engagement'], stat: '90%+ open rate within minutes' },
    { icon: <MessageSquare size={40} />, title: 'SMS Marketing', description: 'Send text messages to promote specials, events, and time-sensitive offers.', benefits: ['Direct line to customers', 'Perfect for last-minute deals', 'Two-way messaging support', 'Compliance with SMS regulations'], stat: '98% open rate on text messages' },
    { icon: <Database size={40} />, title: 'Customer Database Ownership', description: 'Own your customer data. Build lasting relationships instead of renting access.', benefits: ['Full customer contact information', 'Order history and preferences', 'Export data anytime', 'Lifetime value tracking'], stat: 'Your data, your customers' },
    { icon: <DollarSign size={40} />, title: 'No Commission Fees', description: 'Unlike delivery apps, we never take a cut of your sales. Keep 100% of every order.', benefits: ['Transparent monthly pricing', 'No hidden fees or charges', 'No per-transaction costs', 'Predictable operating expenses'], stat: 'Save $1,000s per month' },
    { icon: <BarChart size={40} />, title: 'Analytics & Insights', description: 'Track sales, understand customer behavior, and make data-driven decisions.', benefits: ['Real-time sales dashboard', 'Customer behavior insights', 'Popular items and trends', 'Marketing campaign performance'], stat: 'Make smarter business decisions' },
  ];

  const included = ['Setup and launch support', 'Unlimited menu updates', 'Payment processing integration', 'Marketing materials (posters, table tents)', 'Staff training and onboarding', '24/7 technical support', 'Regular platform updates', 'Mobile-optimized design'];

  return (
    <div>
      {/* Hero */}
      <section className="halftone-bg" style={{ background: '#FFD700', borderBottom: '4px solid #000', padding: '5rem 1.5rem', textAlign: 'center', position: 'relative' }}>
        <div className="container" style={{ maxWidth: '800px', position: 'relative', zIndex: 1 }}>
          <h1 className="heading-1" style={{ marginBottom: '1.5rem', color: '#000' }}>Everything Your Restaurant Needs to Thrive</h1>
          <p style={{ fontSize: '1.25rem', color: '#333', fontWeight: 600 }}>Professional mobile technology that helps you compete with the big chains and own your customer relationships.</p>
        </div>
      </section>

      {/* Features Grid */}
      <section style={{ padding: '5rem 1.5rem', background: '#fff', borderBottom: '4px solid #000' }}>
        <div className="container">
          {features.map((feature, index) => (
            <div key={index} style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '3rem',
              alignItems: 'center',
              marginBottom: '4rem',
              paddingBottom: index < features.length - 1 ? '4rem' : 0,
              borderBottom: index < features.length - 1 ? '4px solid #000' : 'none',
            }} className="feature-row">
              <div style={{ order: index % 2 === 0 ? 1 : 2 }}>
                <div style={{ width: '64px', height: '64px', borderRadius: '8px', background: '#FFD700', border: '3px solid #000', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '1.5rem', boxShadow: '4px 4px 0px 0px #000' }}>
                  {feature.icon}
                </div>
                <h2 style={{ fontFamily: "'Bangers', cursive", fontSize: '2rem', marginBottom: '1rem', color: '#000', letterSpacing: '0.03em' }}>{feature.title}</h2>
                <p style={{ color: '#444', marginBottom: '1.5rem', fontWeight: 500, fontSize: '1.05rem', lineHeight: 1.6 }}>{feature.description}</p>
                <ul style={{ listStyle: 'none', padding: 0, marginBottom: '1.5rem' }}>
                  {feature.benefits.map((b, j) => (
                    <li key={j} style={{ display: 'flex', gap: '0.75rem', marginBottom: '0.75rem', alignItems: 'flex-start' }}>
                      <Check size={20} color="#6A0DAD" strokeWidth={3} style={{ flexShrink: 0, marginTop: '0.125rem' }} />
                      <span style={{ color: '#333', fontWeight: 600 }}>{b}</span>
                    </li>
                  ))}
                </ul>
                <div style={{ padding: '0.75rem 1rem', background: '#FFF9DB', borderRadius: '6px', border: '3px solid #000', display: 'inline-block', boxShadow: '3px 3px 0px 0px #000' }}>
                  <span style={{ fontWeight: 800, color: '#6A0DAD', textTransform: 'uppercase', fontSize: '0.9rem' }}>{feature.stat}</span>
                </div>
              </div>
              <div style={{
                order: index % 2 === 0 ? 2 : 1,
                borderRadius: '12px',
                height: '350px',
                overflow: 'hidden',
                border: '4px solid #000',
                boxShadow: '8px 8px 0px 0px #000',
              }}>
                <img
                  src={FEATURE_IMAGES[index]}
                  alt={feature.title}
                  style={{ width: '100%', height: '100%', objectFit: index === 6 ? 'contain' : 'cover', background: index === 6 ? '#fff' : 'transparent' }}
                />
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Self-Ordering Kiosk */}
      <section data-testid="kiosk-section" style={{ padding: '5rem 1.5rem', background: '#FFF9DB', borderBottom: '4px solid #000' }}>
        <div className="container" style={{ maxWidth: '1100px' }}>
          <h2 className="heading-2" style={{ marginBottom: '1rem', color: '#000' }}>Self-Ordering Kiosk</h2>
          <p style={{ fontSize: '1.125rem', color: '#444', maxWidth: '650px', marginBottom: '3rem', lineHeight: 1.6, fontWeight: 500 }}>
            Give your customers a fast, contactless way to order right from your restaurant floor. Boost average order value by 30% and cut wait times in half.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '2rem', marginBottom: '3rem' }}>
            <div style={{ borderRadius: '12px', overflow: 'hidden', border: '4px solid #000', boxShadow: '8px 8px 0px 0px #000', height: '360px' }}>
              <img src={KIOSK_IMAGE} alt="Self-ordering kiosk" data-testid="kiosk-image-1" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              {[
                { stat: '30%', label: 'Higher Average Order Value', desc: 'Smart upsell prompts suggest add-ons, combos, and upgrades on every order.' },
                { stat: '40%', label: 'Faster Order Times', desc: 'Customers browse, customize, and pay in under 60 seconds.' },
                { stat: '99%', label: 'Order Accuracy', desc: 'Eliminate miscommunication — customers enter exactly what they want.' },
                { stat: '61%', label: 'Of Diners Want More Kiosks', desc: '2025 survey shows growing consumer demand for self-service ordering.' },
              ].map((item, idx) => (
                <div key={idx} className="comic-card" style={{ display: 'flex', gap: '1rem', alignItems: 'flex-start', padding: '1rem' }}>
                  <div style={{ fontFamily: "'Bangers', cursive", minWidth: '56px', fontSize: '1.5rem', color: '#6A0DAD', letterSpacing: '0.02em' }}>{item.stat}</div>
                  <div>
                    <div style={{ fontWeight: 800, color: '#000', fontSize: '0.95rem', textTransform: 'uppercase' }}>{item.label}</div>
                    <div style={{ color: '#555', fontSize: '0.85rem', fontWeight: 500, lineHeight: 1.45 }}>{item.desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem', marginBottom: '2.5rem' }}>
            {['Touchscreen Ordering', 'Contactless Payment', 'Menu Customization', 'Kitchen Display Integration', 'Multi-Language Support', 'Real-Time Inventory Sync', 'Upsell & Cross-Sell Prompts', 'Analytics Dashboard'].map((pill, idx) => (
              <span key={idx} style={{ padding: '8px 16px', borderRadius: '4px', background: '#FFD700', border: '3px solid #000', fontWeight: 700, fontSize: '0.85rem', textTransform: 'uppercase', boxShadow: '2px 2px 0px 0px #000' }}>{pill}</span>
            ))}
          </div>
          <div style={{ padding: '1.5rem 2rem', borderRadius: '12px', background: '#000', border: '4px solid #000', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '1.5rem' }}>
            <div>
              <div style={{ fontFamily: "'Bangers', cursive", color: '#FFD700', fontSize: '1.5rem', letterSpacing: '0.03em' }}>Ready to modernize your ordering?</div>
              <div style={{ color: 'rgba(255,255,255,0.7)', fontWeight: 500 }}>Add KioskMates to your HungrMates package today.</div>
            </div>
            <Link to="/pricing" className="btn-primary" data-testid="kiosk-cta-btn" style={{ whiteSpace: 'nowrap' }}>See Pricing</Link>
          </div>
        </div>
      </section>

      {/* What's Included */}
      <section style={{ padding: '5rem 1.5rem', background: '#fff', borderBottom: '4px solid #000' }}>
        <div className="container" style={{ maxWidth: '900px' }}>
          <h2 className="heading-2" style={{ textAlign: 'center', marginBottom: '3rem', color: '#000' }}>Everything Included in Your Plan</h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1rem' }}>
            {included.map((item, i) => (
              <div key={i} className="comic-card" style={{ padding: '1rem', display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
                <Check size={22} color="#6A0DAD" strokeWidth={3} style={{ flexShrink: 0 }} />
                <span style={{ fontWeight: 700 }}>{item}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="halftone-bg" style={{ padding: '5rem 1.5rem', background: '#FFF9DB', borderBottom: '4px solid #000', position: 'relative' }}>
        <div className="container" style={{ position: 'relative', zIndex: 1 }}>
          <h2 className="heading-2" style={{ textAlign: 'center', marginBottom: '3rem', color: '#000' }}>The Numbers Don't Lie</h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1.5rem' }}>
            {[
              { stat: '80%', label: 'Of internet users own a smartphone' },
              { stat: '70%', label: 'Of mobile searches result in action within an hour' },
              { stat: '10x', label: 'Higher redemption rate for mobile coupons vs print' },
              { stat: '78%', label: 'Of consumers influenced by coupons' },
              { stat: '67%', label: 'More spending from repeat customers' },
              { stat: '84%', label: 'Of small businesses see immediate benefits from mobile' },
            ].map((item, i) => (
              <div key={i} className="comic-card" style={{ textAlign: 'center', padding: '1.5rem' }}>
                <div style={{ fontFamily: "'Bangers', cursive", fontSize: '2.5rem', color: '#6A0DAD', letterSpacing: '0.03em' }}>{item.stat}</div>
                <div style={{ fontSize: '0.875rem', color: '#444', fontWeight: 600, lineHeight: 1.4 }}>{item.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="halftone-bg" style={{ padding: '5rem 1.5rem', background: '#6A0DAD', textAlign: 'center', position: 'relative' }}>
        <div className="container" style={{ maxWidth: '600px', position: 'relative', zIndex: 1 }}>
          <h2 className="heading-2" style={{ marginBottom: '1.5rem', color: '#FFD700' }}>See HungrMates in Action</h2>
          <p style={{ color: 'rgba(255,255,255,0.85)', marginBottom: '2rem', fontWeight: 500, fontSize: '1.125rem' }}>Book a free demo and discover how these features can transform your restaurant.</p>
          <Link to="/book-demo" className="btn-primary" style={{ fontSize: '1.125rem', padding: '16px 32px' }}>
            Book a Free Demo <ArrowRight size={20} style={{ marginLeft: '0.5rem' }} />
          </Link>
        </div>
      </section>

      <style>{`
        @media (max-width: 768px) {
          .feature-row { grid-template-columns: 1fr !important; }
          .feature-row > div { order: 1 !important; }
        }
      `}</style>
    </div>
  );
};
